import { CDPUser, UserInfo, UserPasswordPolicy, UserPasswordTicketResponse } from '../../models/user';
import { audiencerateApi } from '../audiencerate';

const usersApi = audiencerateApi.injectEndpoints({
  endpoints: (builder) => ({
    getUserInfo: builder.query<UserInfo, void>({
      query: () => ({
        url: `/v1/userinfo`,
      }),
      providesTags: ['userInfo'],
    }),
    getUserPasswordPolicy: builder.query<UserPasswordPolicy, void>({
      query: () => ({
        url: `/v1/users/self/password-policy`,
      }),
    }),
    createPasswordTicket: builder.mutation<UserPasswordTicketResponse, void>({
      query: () => {
        return {
          url: `/v1/users/self/change-password-ticket`,
          method: 'POST',
          body: {
            callback: window.location.origin + '/cb?type=password-change',
            ttlSeconds: 1 * 60 * 60, // 1 hour
          },
        };
      },
    }),
    updateCurrentUser: builder.mutation<CDPUser, UpdateCurrentUserBody>({
      query: (body) => ({
        url: `/v1/users/self`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['userInfo', 'WorkspaceUsers', 'CompanyUsers', 'Terms'],
    }),
    changeUserPassword: builder.mutation<any, { userId: string }>({
      query: ({ userId }) => ({
        url: `/v1/users/${userId}/change-password`,
        method: 'POST',
      }),
    }),
    changeUserInfo: builder.mutation<CDPUser, ChangeUserInfoBody>({
      query: ({ userId, ...body }) => ({
        url: `/v1/users/${userId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['userInfo', 'WorkspaceUsers', 'CompanyUsers', 'Terms'],
    }),
    passwordRecovery: builder.mutation<unknown, PasswordRecoveryRequestBody>({
      query: (body) => ({
        url: `/v1/users/password-recovery`,
        method: 'POST',
        body,
      }),
      transformErrorResponse: (response: any) => {
        return { status: response?.data || 'An error occurred' };
      },
    }),
  }),
  overrideExisting: false,
});

type UpdateCurrentUserBody = {
  firstName?: string;
  lastName?: string;
  email?: string;
  termsAcceptedAt?: Date;
};

type ChangeUserInfoBody = {
  userId: string;
} & UpdateCurrentUserBody;

type PasswordRecoveryRequestBody = {
  email: string;
};

export const {
  useChangeUserInfoMutation,
  useChangeUserPasswordMutation,
  useGetUserInfoQuery,
  useLazyGetUserInfoQuery,
  useGetUserPasswordPolicyQuery,
  useLazyGetUserPasswordPolicyQuery,
  useCreatePasswordTicketMutation,
  usePasswordRecoveryMutation,
  useUpdateCurrentUserMutation,
} = usersApi;
